import { useCallback, useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { DropDownList, DropDownListFilterChangeEvent } from '@progress/kendo-react-dropdowns'
import { DatePicker } from '@progress/kendo-react-dateinputs'
import { Input, Switch } from '@progress/kendo-react-inputs'
import { Dialog, DialogActionsBar } from '@progress/kendo-react-dialogs'
import { FaChevronLeft, FaEnvelope } from 'react-icons/fa'
import './InvoicePage.css'

import InvoiceLinesEditor, {
  InvoiceLineDraft,
  lineAmount,
  lineProblems,
  newInvoiceLine,
} from '../../components/FormComps/Invoicing/Invoice/InvoiceLinesEditor'
import AccountForm from '../../components/FormComps/Accounts/AccountForm'
import Notes from '../Notes/Notes'
import SkeletonForm from '../../components/Skeletons/SkeletonForm'
import {
  deleteInvoice,
  emailInvoice,
  EmailTemplateOption,
  getEmailTemplateOptions,
  getInvoiceDetails,
  getInvoiceEmailPreview,
  getInvoiceLines,
  InvoiceEmailPreview,
  submitInvoiceWithItems,
  updateInvoiceWithItems,
} from '../../data/invoiceData'
import { getClientsDropdown } from '../../data/clientData'
import { getAddressesForClientDropdown, getClientsForClientsDropdown } from '../../data/accountsData'
import { getPaymentTermDropdown } from '../../data/paymentTermData'
import { getCurrentUserEmployee } from '../../data/employeeData'
import { dropDown } from '../../data/interfaces'
import { useToast } from '../../contexts/ToastContext'
import { getApiErrorDetails } from '../../helpers/apiError'

interface InvoicePageProps {
  lockEdit: boolean
}

interface InvoiceHeader {
  clientId: number
  contactId: number
  siteId: number
  useCareOf: boolean
  careOfTitle: string
  paymentTermId: number
  invoiceDate: Date | null
  documentTypeId: number
  emailTemplateId: number
}

const emptyHeader: InvoiceHeader = {
  clientId: 0,
  contactId: 0,
  siteId: 0,
  useCareOf: false,
  careOfTitle: '',
  paymentTermId: 0,
  invoiceDate: new Date(),
  documentTypeId: 1,
  emailTemplateId: 0,
}

const DOCUMENT_TYPES = [
  { text: 'Invoice', value: 1 },
  { text: 'Credit', value: 2 },
]

const toOptions = (items: dropDown[]) => items.map((i) => ({ text: i.text, value: Number(i.value) }))

interface EmailedRecord {
  date: string
  to: string | null
  by: string | null
}

const formatDateTime = (value: string) =>
  new Date(value).toLocaleString('en-GB', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })


/**
 * One page for creating and editing an invoice - its details and all of its
 * lines together, the way Xero lays out an invoice. /invoicing/new creates one;
 * /invoicing/:invoiceId opens an existing one. Nothing is saved until Save.
 */
const InvoicePage = ({ lockEdit }: InvoicePageProps) => {
  const { invoiceId: invoiceIdParam } = useParams<{ invoiceId: string }>()
  const invoiceId = invoiceIdParam && invoiceIdParam !== 'new' ? Number(invoiceIdParam) : null
  const isNew = invoiceId === null
  const navigate = useNavigate()
  const { showToast } = useToast()

  const [loading, setLoading] = useState(!isNew)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const [dirty, setDirty] = useState(false)
  const [showErrors, setShowErrors] = useState(false)

  const [refFormatted, setRefFormatted] = useState('')
  const [xeroId, setXeroId] = useState<string | null>(null)
  const [savedOrderNo, setSavedOrderNo] = useState<string | null>(null)
  // The most recent time the invoice was emailed to its contact.
  const [emailed, setEmailed] = useState<EmailedRecord | null>(null)
  // Open once the preview has loaded; sending needs the user to confirm it.
  const [emailPreview, setEmailPreview] = useState<InvoiceEmailPreview | null>(null)
  // The subject and message as the user edits them in the send dialog,
  // starting from the template. These are what get sent.
  const [emailSubject, setEmailSubject] = useState('')
  const [emailBody, setEmailBody] = useState('')
  const [preparingEmail, setPreparingEmail] = useState(false)
  const [sendingEmail, setSendingEmail] = useState(false)
  const [selectedTab, setSelectedTab] = useState<'invoice' | 'notes'>('invoice')
  const [header, setHeaderState] = useState<InvoiceHeader>(emptyHeader)
  const [lines, setLinesState] = useState<InvoiceLineDraft[]>(() => [newInvoiceLine()])

  const [clients, setClients] = useState<dropDown[]>([])
  const [contacts, setContacts] = useState<dropDown[]>([])
  const [sites, setSites] = useState<dropDown[]>([])
  const [paymentTerms, setPaymentTerms] = useState<dropDown[]>([])
  const [emailTemplates, setEmailTemplates] = useState<EmailTemplateOption[]>([])
  const [canCreateCredits, setCanCreateCredits] = useState(false)

  const [accountDialog, setAccountDialog] = useState<{ mode: 'add' | 'edit'; id: number | null } | null>(null)

  // Every edit goes through these, so leaving with unsaved changes can warn.
  const setHeader = (patch: Partial<InvoiceHeader>) => {
    setHeaderState((prev) => ({ ...prev, ...patch }))
    setDirty(true)
  }
  const setLines: typeof setLinesState = (update) => {
    setLinesState(update)
    setDirty(true)
  }

  // --- Reference data --------------------------------------------------------

  useEffect(() => {
    getPaymentTermDropdown().then((res) => setPaymentTerms(toOptions(res))).catch(() => setPaymentTerms([]))
    getClientsDropdown('').then((res) => setClients(toOptions(res))).catch(() => setClients([]))
    getCurrentUserEmployee()
      .then((user) =>
        setCanCreateCredits(
          !!user?.permissions?.some((p: any) => p.area === 'Sales' && p.permission === 'CreateCredits' && p.assigned)
        )
      )
      .catch(() => setCanCreateCredits(false))
  }, [])

  const loadClientChildren = useCallback(async (clientId: number, autoSelect: boolean) => {
    if (!clientId) {
      setContacts([])
      setSites([])
      return
    }
    const [contactRes, siteRes] = await Promise.all([
      getClientsForClientsDropdown(clientId).catch(() => [] as dropDown[]),
      getAddressesForClientDropdown(clientId).catch(() => [] as dropDown[]),
    ])
    const contactOptions = toOptions(contactRes)
    const siteOptions = toOptions(siteRes)
    setContacts(contactOptions)
    setSites(siteOptions)
    // A client with only one contact or address has it chosen for them.
    if (autoSelect) {
      setHeaderState((prev) => ({
        ...prev,
        contactId: contactOptions.length === 1 ? contactOptions[0].value : prev.contactId,
        siteId: siteOptions.length === 1 ? siteOptions[0].value : prev.siteId,
      }))
    }
  }, [])

  // --- Loading an existing invoice -------------------------------------------

  const loadInvoice = useCallback(async (id: number) => {
    setLoading(true)
    setLoadError(null)
    try {
      const [details, savedLines] = await Promise.all([getInvoiceDetails(id), getInvoiceLines(id)])
      setRefFormatted(details.refFormatted)
      setXeroId(details.xeroId ?? null)
      setSavedOrderNo(details.orderNo ?? null)
      setEmailed(
        details.emailedDate
          ? { date: details.emailedDate, to: details.emailedTo, by: details.emailedBy }
          : null
      )
      setHeaderState({
        clientId: details.clientId,
        contactId: details.contactId ?? 0,
        siteId: details.siteId ?? 0,
        useCareOf: !!details.careOfTitle,
        careOfTitle: details.careOfTitle ?? '',
        paymentTermId: details.paymentTermId,
        invoiceDate: details.invoiceDate ? new Date(details.invoiceDate) : null,
        documentTypeId: details.documentTypeId || 1,
        emailTemplateId: details.emailTemplateId ?? 0,
      })
      // Listed with its current template, which may since have been retired.
      getEmailTemplateOptions(details.emailTemplateId)
        .then(setEmailTemplates)
        .catch(() => setEmailTemplates([]))
      // The saved client may be outside the first page of the client list.
      setClients((prev) =>
        prev.some((c) => c.value === details.clientId)
          ? prev
          : [{ text: details.client, value: details.clientId }, ...prev]
      )
      setLinesState(
        savedLines.length > 0
          ? savedLines.map((l) => ({
              ...newInvoiceLine(),
              invoiceitemId: l.invoiceitemId,
              jobId: l.jobId,
              jobLabel: l.jobRefFormatted ?? undefined,
              description: l.description ?? '',
              orderNo: l.orderNo,
              itemValue: l.itemValue,
              xeroDepartmentId: l.xeroDepartmentId,
              xeroTaxRateId: l.xeroTaxRateId,
            }))
          : [newInvoiceLine()]
      )
      await loadClientChildren(details.clientId, false)
      setDirty(false)
      setShowErrors(false)
    } catch (error) {
      setLoadError(getApiErrorDetails(error, "This invoice couldn't be loaded.").message)
    } finally {
      setLoading(false)
    }
  }, [loadClientChildren])

  useEffect(() => {
    if (invoiceId) {
      loadInvoice(invoiceId)
    } else {
      getEmailTemplateOptions()
        .then(setEmailTemplates)
        .catch(() => setEmailTemplates([]))
      setHeaderState(emptyHeader)
      setLinesState([newInvoiceLine()])
      setRefFormatted('')
      setXeroId(null)
      setSavedOrderNo(null)
      setEmailed(null)
      setDirty(false)
      setLoading(false)
    }
  }, [invoiceId, loadInvoice])

  // A new invoice starts on the default email template. Set without marking
  // the invoice changed - it is a starting value, like today's date.
  useEffect(() => {
    if (!isNew || header.emailTemplateId) return
    const fallback = emailTemplates.find((t) => t.isDefault) ?? emailTemplates[0]
    if (fallback) setHeaderState((prev) => ({ ...prev, emailTemplateId: fallback.emailTemplateId }))
  }, [isNew, emailTemplates, header.emailTemplateId])

  // Closing the tab or reloading with unsaved changes asks first.
  useEffect(() => {
    if (!dirty) return
    const warn = (e: BeforeUnloadEvent) => {
      e.preventDefault()
      e.returnValue = ''
    }
    window.addEventListener('beforeunload', warn)
    return () => window.removeEventListener('beforeunload', warn)
  }, [dirty])

  // --- Actions ----------------------------------------------------------------

  const backToList = () => {
    if (dirty && !window.confirm('You have unsaved changes to this invoice. Leave without saving?')) return
    navigate('/invoicing')
  }

  const headerProblems = (): string[] => {
    const problems: string[] = []
    if (!header.clientId) problems.push('client')
    if (!header.contactId) problems.push('contact')
    if (!header.siteId) problems.push('address')
    if (!header.paymentTermId) problems.push('payment term')
    if (!header.invoiceDate) problems.push('invoice date')
    if (!header.emailTemplateId) problems.push('email template')
    return problems
  }

  const handleSave = async () => {
    const missingHeader = headerProblems()
    const incompleteLines = lines.some((l) => lineProblems(l).length > 0)
    if (missingHeader.length > 0 || incompleteLines) {
      setShowErrors(true)
      showToast(
        missingHeader.length > 0
          ? `Please choose the ${missingHeader.join(', ')}.`
          : 'Every line needs a description, a Xero department and a tax rate.',
        'error'
      )
      return
    }

    // Stored at 03:30 rather than midnight so a timezone shift can't move it
    // onto the previous day.
    const invoiceDate = new Date(header.invoiceDate!)
    invoiceDate.setHours(3, 30, 0, 0)

    const payload = {
      careOfTitle: header.useCareOf ? header.careOfTitle.trim() || null : null,
      orderNo: null,
      clientId: header.clientId,
      siteId: header.siteId,
      contactId: header.contactId,
      invoiceDate,
      paymentTermId: header.paymentTermId,
      documentTypeId: header.documentTypeId,
      emailTemplateId: header.emailTemplateId,
      items: lines.map((l) => ({
        invoiceitemId: l.invoiceitemId,
        jobId: l.jobId,
        description: l.description,
        orderNo: l.orderNo,
        itemValue: lineAmount(l),
        xeroDepartmentId: l.xeroDepartmentId,
        xeroTaxRateId: l.xeroTaxRateId,
      })),
    }

    setSaving(true)
    try {
      const result = isNew
        ? await submitInvoiceWithItems(payload)
        : await updateInvoiceWithItems(invoiceId!, payload)

      setDirty(false)
      if (result.xeroError) {
        showToast(`Invoice saved, but it couldn't be sent to Xero: ${result.xeroError}`, 'error')
      } else {
        showToast(isNew ? 'Invoice created' : 'Invoice saved', 'success')
      }

      if (isNew) {
        // Carry on from the saved invoice, now editable at its own address.
        navigate(`/invoicing/${result.invoice.invoiceId}`, { replace: true })
      } else {
        await loadInvoice(invoiceId!)
      }
    } catch (error) {
      const { message, details } = getApiErrorDetails(error, "The invoice couldn't be saved.")
      showToast([message, ...details].join(' '), 'error')
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async () => {
    if (!invoiceId) return
    if (!window.confirm(`Delete invoice ${refFormatted}? If it has been sent to Xero it will be voided there.`)) return
    try {
      await deleteInvoice(invoiceId)
      showToast('Invoice deleted', 'success')
      setDirty(false)
      navigate('/invoicing')
    } catch (error) {
      showToast(getApiErrorDetails(error, "The invoice couldn't be deleted.").message, 'error')
    }
  }

  // The email goes out from what is saved - its contact and its PDF - so
  // unsaved changes have to be saved first or they would not be in it.
  const handleEmailClick = async () => {
    if (!invoiceId) return
    if (dirty) {
      showToast('Save your changes before emailing the invoice.', 'info')
      return
    }
    setPreparingEmail(true)
    try {
      const preview = await getInvoiceEmailPreview(invoiceId)
      setEmailSubject(preview.subject)
      setEmailBody(preview.body)
      setEmailPreview(preview)
    } catch (error) {
      showToast(getApiErrorDetails(error, "The email couldn't be prepared.").message, 'error')
    } finally {
      setPreparingEmail(false)
    }
  }

  const handleSendEmail = async () => {
    if (!invoiceId) return
    setSendingEmail(true)
    try {
      const result = await emailInvoice(invoiceId, { subject: emailSubject.trim(), body: emailBody })
      setEmailed({ date: result.emailedDate, to: result.emailedTo, by: result.emailedBy })
      setEmailPreview(null)
      showToast(`${isCredit ? 'Credit' : 'Invoice'} emailed to ${result.emailedTo}`, 'success')
    } catch (error) {
      showToast(getApiErrorDetails(error, "The invoice couldn't be emailed.").message, 'error')
    } finally {
      setSendingEmail(false)
    }
  }

  const handleClientFilter = async (e: DropDownListFilterChangeEvent) => {
    try {
      const res = toOptions(await getClientsDropdown(e.filter.value))
      const current = clients.find((c) => c.value === header.clientId)
      setClients(current && !res.some((c) => c.value === current.value) ? [current, ...res] : res)
    } catch {
      // Keep the current list; the search can be retried.
    }
  }

  const handleClientChange = (clientId: number) => {
    setHeader({ clientId, contactId: 0, siteId: 0 })
    loadClientChildren(clientId, true)
  }

  const closeAccountDialog = () => {
    setAccountDialog(null)
    // Pick up anything added in the dialog.
    getClientsDropdown('').then((res) => {
      const options = toOptions(res)
      const current = clients.find((c) => c.value === header.clientId)
      setClients(current && !options.some((c) => c.value === current.value) ? [current, ...options] : options)
    })
    loadClientChildren(header.clientId, false)
  }

  // --- Rendering --------------------------------------------------------------

  if (isNew && lockEdit) {
    return <div style={{ padding: '2rem' }}>You don't have permission to create invoices.</div>
  }

  const missing = showErrors ? headerProblems() : []
  const fieldClass = (field: string, extra = '') =>
    `inv-field${extra}${missing.includes(field) ? ' inv-invalid' : ''}`
  const required = <em>*</em>

  const isCredit = header.documentTypeId === 2
  const kind = isCredit ? 'credit' : 'invoice'
  const title = isNew ? `New ${kind}` : `${isCredit ? 'Credit' : 'Invoice'} ${refFormatted}`

  // Like Xero's Reference: the invoice's order number, which an invoice without
  // one takes from its first line's job when saved.
  const reference = savedOrderNo || lines.find((l) => l.orderNo)?.orderNo || null

  // A retired template is only listed when it is this invoice's own.
  const emailTemplateItems = emailTemplates.map((t) => ({
    ...t,
    label: t.isRetired ? `${t.name} (retired)` : t.name,
  }))

  const sheet = (
    <section className='inv-sheet'>
      <div className='inv-headfields'>
        <div className={fieldClass('client')}>
          <label className='inv-field__label'>To{required}</label>
          <DropDownList
            data={clients}
            textField='text'
            dataItemKey='value'
            filterable={true}
            onFilterChange={handleClientFilter}
            value={clients.find((c) => c.value === header.clientId) ?? null}
            defaultItem={{ text: 'Choose a client', value: 0 }}
            disabled={lockEdit}
            onChange={(e) => handleClientChange(Number(e.value?.value ?? 0))}
          />
          {!lockEdit && (
            <button type='button' className='inv-link' onClick={() => setAccountDialog({ mode: 'add', id: null })}>
              + New client
            </button>
          )}
        </div>

        <div className={fieldClass('contact')}>
          <label className='inv-field__label'>Contact{required}</label>
          <DropDownList
            data={contacts}
            textField='text'
            dataItemKey='value'
            value={contacts.find((c) => c.value === header.contactId) ?? null}
            defaultItem={{ text: header.clientId ? 'Choose a contact' : 'Choose a client first', value: 0 }}
            disabled={lockEdit || !header.clientId}
            onChange={(e) => setHeader({ contactId: Number(e.value?.value ?? 0) })}
          />
          {!lockEdit && header.clientId > 0 && (
            <button type='button' className='inv-link' onClick={() => setAccountDialog({ mode: 'edit', id: header.clientId })}>
              + New contact
            </button>
          )}
        </div>

        <div className={fieldClass('invoice date')}>
          <label className='inv-field__label'>Date{required}</label>
          <DatePicker
            value={header.invoiceDate}
            format='d MMM yyyy'
            disabled={lockEdit}
            onChange={(e) => setHeader({ invoiceDate: e.value })}
          />
        </div>

        <div className={fieldClass('payment term')}>
          <label className='inv-field__label'>Payment term{required}</label>
          <DropDownList
            data={paymentTerms}
            textField='text'
            dataItemKey='value'
            value={paymentTerms.find((p) => p.value === header.paymentTermId) ?? null}
            defaultItem={{ text: 'Choose', value: 0 }}
            disabled={lockEdit}
            onChange={(e) => setHeader({ paymentTermId: Number(e.value?.value ?? 0) })}
          />
        </div>

        <div className='inv-field'>
          <label className='inv-field__label'>{isCredit ? 'Credit' : 'Invoice'} number</label>
          {isNew ? (
            <div className='inv-field__readonly inv-field__readonly--placeholder'>Assigned when saved</div>
          ) : (
            <div className='inv-field__readonly'>{refFormatted}</div>
          )}
        </div>

        <div className='inv-field'>
          <label className='inv-field__label'>Reference</label>
          {reference ? (
            <div className='inv-field__readonly'>{reference}</div>
          ) : (
            <div className='inv-field__readonly inv-field__readonly--placeholder'>From the first job</div>
          )}
        </div>
      </div>

      <div className='inv-headfields inv-headfields--secondary'>
        <div className={fieldClass('address')}>
          <label className='inv-field__label'>Address{required}</label>
          <DropDownList
            data={sites}
            textField='text'
            dataItemKey='value'
            value={sites.find((s) => s.value === header.siteId) ?? null}
            defaultItem={{ text: header.clientId ? 'Choose an address' : 'Choose a client first', value: 0 }}
            disabled={lockEdit || !header.clientId}
            onChange={(e) => setHeader({ siteId: Number(e.value?.value ?? 0) })}
          />
          {!lockEdit && header.clientId > 0 && (
            <button type='button' className='inv-link' onClick={() => setAccountDialog({ mode: 'edit', id: header.clientId })}>
              + New address
            </button>
          )}
        </div>

        <div className='inv-field'>
          <label className='inv-field__label'>Type</label>
          {canCreateCredits && !lockEdit ? (
            <DropDownList
              data={DOCUMENT_TYPES}
              textField='text'
              dataItemKey='value'
              value={DOCUMENT_TYPES.find((t) => t.value === header.documentTypeId)}
              onChange={(e) => setHeader({ documentTypeId: Number(e.value.value) })}
            />
          ) : (
            <div className='inv-field__readonly'>{isCredit ? 'Credit' : 'Invoice'}</div>
          )}
        </div>

        <div className={fieldClass('email template')}>
          <label className='inv-field__label'>Email template{required}</label>
          <DropDownList
            data={emailTemplateItems}
            textField='label'
            dataItemKey='emailTemplateId'
            value={emailTemplateItems.find((t) => t.emailTemplateId === header.emailTemplateId) ?? null}
            defaultItem={{ label: 'Choose a template', emailTemplateId: 0 }}
            disabled={lockEdit}
            onChange={(e) => setHeader({ emailTemplateId: Number(e.value?.emailTemplateId ?? 0) })}
          />
        </div>

        <div className='inv-field'>
          <label className='inv-field__label'>Care of (C/O)</label>
          <div className='inv-careof'>
            <Switch
              checked={header.useCareOf}
              disabled={lockEdit}
              onChange={(e) => setHeader({ useCareOf: e.value })}
            />
            {header.useCareOf && (
              <Input
                value={header.careOfTitle}
                placeholder='e.g. c/o Managing Agent Ltd'
                disabled={lockEdit}
                onChange={(e) => setHeader({ careOfTitle: String(e.value ?? '') })}
                style={{ flex: 1 }}
              />
            )}
          </div>
        </div>
      </div>

      {missing.length > 0 && <div className='inv-error'>Please choose the {missing.join(', ')}.</div>}

      <div className='inv-amounts-are'>
        Amounts are <strong>Tax exclusive</strong>
      </div>

      <InvoiceLinesEditor
        clientId={header.clientId}
        lines={lines}
        setLines={setLines}
        showErrors={showErrors}
        readOnly={lockEdit}
      />
      {showErrors && lines.some((l) => lineProblems(l).length > 0) && (
        <div className='inv-error'>
          Every line needs a description, a department and a tax rate - the missing ones are highlighted.
        </div>
      )}
    </section>
  )

  return (
    <div className='inv-page'>
      <header className='inv-topbar'>
        <div className='inv-topbar__inner'>
          <div>
            <button type='button' className='inv-crumb' onClick={backToList}>
              <FaChevronLeft /> Sales
            </button>
            <div className='inv-title-row'>
              <h1 className='inv-title'>{loading ? 'Loading…' : title}</h1>
              {!loading && !loadError && (
                <>
                  {isCredit && <span className='inv-status inv-status--credit'>Credit</span>}
                  {isNew ? (
                    <span className='inv-status'>Draft</span>
                  ) : xeroId ? (
                    <span className='inv-status inv-status--xero'>In Xero</span>
                  ) : (
                    <span className='inv-status'>Not sent to Xero</span>
                  )}
                  {emailed && (
                    <span
                      className='inv-status inv-status--emailed'
                      title={[emailed.to && `To ${emailed.to}`, emailed.by && `by ${emailed.by}`].filter(Boolean).join(' ')}
                    >
                      <FaEnvelope /> Emailed {formatDateTime(emailed.date)}
                    </span>
                  )}
                  {dirty && <span className='inv-unsaved'>Unsaved changes</span>}
                </>
              )}
            </div>
          </div>

          {!lockEdit && !loadError && (
            <div className='inv-actions'>
              {!isNew && (
                <button type='button' className='inv-btn inv-btn--ghost-danger' onClick={handleDelete} disabled={saving}>
                  Delete
                </button>
              )}
              {!isNew && (
                <button
                  type='button'
                  className='inv-btn inv-btn--secondary'
                  onClick={handleEmailClick}
                  disabled={saving || loading || preparingEmail || sendingEmail || dirty}
                  title={dirty ? 'Save your changes before emailing' : `Email this ${kind} to its contact`}
                >
                  <FaEnvelope />
                  {preparingEmail ? 'Preparing…' : 'Email'}
                </button>
              )}
              <button type='button' className='inv-btn inv-btn--secondary' onClick={backToList} disabled={saving}>
                Cancel
              </button>
              <button
                type='button'
                className={`inv-btn ${isNew ? 'inv-btn--accent' : 'inv-btn--primary'}`}
                onClick={handleSave}
                disabled={saving || loading || (!isNew && !dirty)}
              >
                {saving ? 'Saving…' : isNew ? `Create ${kind}` : 'Save'}
              </button>
            </div>
          )}
        </div>
      </header>

      <div className='inv-body'>
        {loading ? (
          <SkeletonForm />
        ) : loadError ? (
          <div className='inv-error-banner'>{loadError}</div>
        ) : (
          <>
            {/* The same Invoice / Notes split as the other record screens. A new
                invoice has no notes yet, so it has no tabs. */}
            {!isNew && (
              <div className='inv-tabs' role='tablist'>
                {([['invoice', isCredit ? 'Credit' : 'Invoice'], ['notes', 'Notes']] as const).map(([tab, label]) => (
                  <button
                    key={tab}
                    type='button'
                    role='tab'
                    aria-selected={selectedTab === tab}
                    className={`inv-tab${selectedTab === tab ? ' inv-tab--active' : ''}`}
                    onClick={() => setSelectedTab(tab)}
                  >
                    {label}
                  </button>
                ))}
              </div>
            )}

            {isNew || selectedTab === 'invoice' ? (
              sheet
            ) : (
              <section className='inv-notes'>
                <Notes
                  lockEdit={lockEdit}
                  sourceEntity={{ enquiryId: null, quoteId: null, jobId: null, invoiceId: invoiceId! }}
                />
              </section>
            )}
          </>
        )}
      </div>

      {emailPreview && (
        <Dialog className='inv-email-dialog' title={`Email ${refFormatted}`} width={560} onClose={() => !sendingEmail && setEmailPreview(null)}>
          <div className='inv-email'>
            {emailPreview.problem ? (
              <div className='inv-error-banner'>{emailPreview.problem}</div>
            ) : emailPreview.to ? (
              <dl className='inv-email__meta'>
                <dt>To</dt>
                <dd>
                  {emailPreview.contactName ? `${emailPreview.contactName} <${emailPreview.to}>` : emailPreview.to}
                </dd>
                <dt>Template</dt>
                <dd>{emailPreview.templateName}</dd>
                <dt>Attachment</dt>
                <dd>{emailPreview.attachmentName}</dd>
              </dl>
            ) : (
              <div className='inv-error-banner'>
                The contact on this {kind} has no email address. Add one to the contact, or choose a
                different contact and save, then try again.
              </div>
            )}
            {emailPreview.to && !emailPreview.problem && (
              <>
                <label className='inv-email__label' htmlFor='inv-email-subject'>Subject</label>
                <input
                  id='inv-email-subject'
                  className='inv-email__input'
                  value={emailSubject}
                  maxLength={500}
                  disabled={sendingEmail}
                  onChange={(e) => setEmailSubject(e.target.value)}
                />
                <label className='inv-email__label' htmlFor='inv-email-body'>Message</label>
                <textarea
                  id='inv-email-body'
                  className='inv-email__input inv-email__body'
                  value={emailBody}
                  rows={10}
                  disabled={sendingEmail}
                  onChange={(e) => setEmailBody(e.target.value)}
                />
                <div className='inv-email__hint'>
                  <span>Changes here apply to this email only. Leave a blank line between paragraphs.</span>
                  {(emailSubject !== emailPreview.subject || emailBody !== emailPreview.body) && (
                    <button
                      type='button'
                      className='inv-link'
                      disabled={sendingEmail}
                      onClick={() => {
                        setEmailSubject(emailPreview.subject)
                        setEmailBody(emailPreview.body)
                      }}
                    >
                      Reset to template
                    </button>
                  )}
                </div>
              </>
            )}
            {emailPreview.to && !emailPreview.problem && emailPreview.emailedDate && (
              <p className='inv-email__resend'>
                Already emailed {formatDateTime(emailPreview.emailedDate)}
                {emailPreview.emailedTo ? ` to ${emailPreview.emailedTo}` : ''}. Sending again will email it again.
              </p>
            )}
          </div>
          <DialogActionsBar>
            <button
              type='button'
              className='inv-btn inv-btn--secondary'
              onClick={() => setEmailPreview(null)}
              disabled={sendingEmail}
            >
              Cancel
            </button>
            <button
              type='button'
              className='inv-btn inv-btn--primary'
              onClick={handleSendEmail}
              disabled={
                sendingEmail || !emailPreview.to || !!emailPreview.problem || !emailSubject.trim() || !emailBody.trim()
              }
            >
              <FaEnvelope />
              {sendingEmail ? 'Sending…' : 'Send'}
            </button>
          </DialogActionsBar>
        </Dialog>
      )}

      {accountDialog && (
        <Dialog width='70vw' height='90vh' title={accountDialog.mode === 'add' ? 'Add new account' : 'Edit account'} onClose={closeAccountDialog}>
          <AccountForm onClose={closeAccountDialog} mode={accountDialog.mode} id={accountDialog.id} setAccountId={() => {}} />
        </Dialog>
      )}
    </div>
  )
}

export default InvoicePage
